/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import { WF_STEP_PROCESSING_TYPE, WF_STEP_TYPE } from '../../../../constants/workflow-steps'
import { isValid } from '../../../../utils/fileNameValidator'

/**
 * Validation schema for Add New Placeholder modal
 * Ported from lego/page/browser/add-document/add-new-document/FormValidationForAddNewPlaceholder.jsx
 */

const ValidationSchema = () => {
  yup.setLocale({
    mixed: {
      required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    }
  })

  yup.addMethod(yup.mixed, 'isValid', function (this: any) {
    return this.test('isValid', 'FILENAME_CONTAINS_INVALID_CHARACTERS', function (values: any) {
      return isValid(values, true)
    })
  })

  yup.addMethod(yup.mixed, 'isNotEmpty', function (this: any) {
    return this.test('isNotEmpty', 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR', function (values: any) {
      let valid = true

      if (!values || values === '') {
        valid = false
      } else if (Array.isArray(values) && !values.length) {
        valid = false
      }

      return valid
    })
  })

  const taskTeamStepLabel = yup
    .object()
    .shape({
      id: yup.number().nullable(),
      label: yup.string().nullable(),
      value: yup.number().nullable()
    })
    .nullable()
    .default(() => null)

  const someAtOnceErrorMessage = 'The # of users cannot be empty or set to 0, it must be set to 1 or more'
  const nofAllValue = yup
    .mixed()
    .when('processingType', {
      is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
      then: (schema: any) =>
        schema
          .transform((value: any) => (isNaN(value) ? undefined : value))
          .test('min', someAtOnceErrorMessage, (value: any) => {
            if (value === null || value === undefined) return false
            return value >= 1
          })
          .required(someAtOnceErrorMessage)
    })
    .nullable()

  return yup.object().shape({
    fileName: yup
      .string()
      .trim()
      .max(255)
      .test('isValid', 'FILENAME_CONTAINS_INVALID_CHARACTERS', (value: any) => isValid(value, true))
      .required()
      .default(() => ''),
    justificationTextId: yup
      .number()
      .min(0)
      .nullable()
      .when('selectedDocTypeObj.requireJustification', {
        is: true,
        then: (schema: any) => schema.required()
      }),
    description: yup
      .string()
      .max(254)
      .required()
      .default(() => ''),
    crdoctype: yup.object().nullable().required(),
    typeData: yup.object().shape({
      initialApprovers: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default(() => [])
      }),
      approvers: yup.object().shape({
        type: yup
          .string()
          .oneOf(Object.values(WF_STEP_TYPE))
          .default(() => WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel: taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue: nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default(() => [])
      })
    })
  })
}

export default ValidationSchema
